import { useEffect, useState } from "react";
import { Download, FileText, Eraser } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusPill } from "@/components/uiext";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";

function kindForStatus(code) {
  if (!code) return "muted";
  if (code >= 500) return "danger";
  if (code >= 400) return "warn";
  if (code >= 300) return "info";
  return "ok";
}

function kindForMethod(method, direction) {
  if (direction === "auth") return "security";
  if (method === "INVITE") return "info";
  if (method === "REGISTER") return "ok";
  if (method === "BYE") return "muted";
  return "info";
}

function downloadWithToken(url, filename) {
  const token = localStorage.getItem("elswitch_token");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      const href = URL.createObjectURL(blob);
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    })
    .catch((e) => toast.error(`Download failed: ${e.message || e}`));
}

/**
 * TracesPane — embeddable live SIP traces viewer (no chrome).
 *
 * Phase 6o (Hotfix G): extracted from TracesSheet so the Trunks editor
 * Diagnostics tab can host the live trace feed inline (no slide-out),
 * matching Vodia/3CX where "traces for this object" live under the
 * object's own Diagnostics panel. TracesSheet still uses this pane
 * internally and keeps serving Live Traffic where an overlay makes
 * sense (no editor context there).
 *
 * Props:
 *   party: { id, name, kind }         — subject to load traces for
 *   pollMs?: number                   — refresh interval (default 4000)
 *   dense?: boolean                   — shrinks paddings for editor embed
 *   active?: boolean                  — gates polling from parent
 *                                        (e.g. only poll while the
 *                                         Diagnostics tab is visible)
 */
export function TracesPane({ party, pollMs = 4000, dense = false, active = true }) {
  const [data, setData] = useState(null);
  const [method, setMethod] = useState("");
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const confirm = useConfirm();

  const load = () => {
    if (!party?.id) return;
    setLoading(true);
    const qs = method ? `?method=${encodeURIComponent(method)}` : "";
    api
      .get(`/parties/${party.id}/traces${qs}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load traces"))
      .finally(() => setLoading(false));
  };

  // Phase 6o Hotfix G2 — manual trace wipe. Destructive, so confirm
  // first; writes an audit log entry server-side. Scoped to the party
  // shown in this pane.
  const clearTraces = async () => {
    if (!party?.id) return;
    const count = data?.traces?.length ?? 0;
    const ok = await confirm({
      title: count > 0
        ? `Clear ${count} trace${count === 1 ? "" : "s"} for ${party.name || "this party"}?`
        : `Clear traces for ${party.name || "this party"}?`,
      description:
        "This removes every captured SIP event for this object from the engine trace buffer. The audit log still records that you cleared them.",
      confirmLabel: "Clear traces",
      danger: true,
    });
    if (!ok) return;
    setClearing(true);
    try {
      const r = await api.delete(`/parties/${party.id}/traces`);
      const deleted = r?.data?.deleted ?? 0;
      toast.success(
        deleted > 0
          ? `Cleared ${deleted} trace${deleted === 1 ? "" : "s"}.`
          : "No traces to clear."
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear traces");
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    if (!active || !party?.id) return;
    load();
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, party?.id, method, pollMs]);

  const padCls = dense ? "" : "mt-4";

  return (
    <div data-testid="traces-pane" className={dense ? "space-y-3" : "space-y-4"}>
      <div className={`flex items-end gap-2 ${padCls}`}>
        <div className="flex-1">
          <Label className="text-xs">Filter by method</Label>
          <Input
            placeholder="e.g. REGISTER, INVITE, BYE"
            value={method}
            onChange={(e) => setMethod(e.target.value.toUpperCase())}
            data-testid="traces-filter-method"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading || !party?.id}
          data-testid="traces-refresh"
        >
          {loading ? "…" : "Refresh"}
        </Button>
        <Button
          size="sm"
          onClick={() =>
            party?.id &&
            downloadWithToken(
              `${API_BASE}/parties/${party.id}/traces.csv${
                method ? `?method=${encodeURIComponent(method)}` : ""
              }`,
              `elswitch-traces-${party.id}.csv`
            )
          }
          disabled={!party?.id}
          data-testid="traces-download"
        >
          <Download className="h-3.5 w-3.5 mr-1" /> CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={clearTraces}
          disabled={clearing || !party?.id || !(data?.traces?.length)}
          className="text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/10 border-[hsl(var(--danger))]/30"
          data-testid="traces-clear"
          title="Delete all traces captured for this object"
        >
          <Eraser className="h-3.5 w-3.5 mr-1" />
          {clearing ? "Clearing…" : "Clear"}
        </Button>
      </div>

      <div className="space-y-2">
        {!party?.id ? (
          <div className="text-xs text-muted-foreground italic">
            Save this trunk first — traces appear once the engine has an
            id to attribute events to.
          </div>
        ) : !data ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : data.traces.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">
            No traces yet. Trigger SIP traffic (REGISTER, INVITE) and it will appear here.
          </div>
        ) : (
          data.traces.map((tr) => {
            const he = tr.headers_excerpt || {};
            const statusLine = he.__status_line__ || "";
            const challenge = he["Proxy-Authenticate"] || he["WWW-Authenticate"] || "";
            const reasonHdr = he["Reason"] || he["Warning"] || "";
            const interestingKeys = Object.keys(he).filter(
              (k) => k !== "__status_line__"
            );
            return (
              <details
                key={tr.id}
                className="rounded border border-border/60 bg-card/50 p-3 text-xs group"
                data-testid={`traces-item-${tr.id}`}
              >
                <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <StatusPill kind={kindForMethod(tr.method, tr.direction)}>
                      {tr.direction || "-"} · {tr.method || "?"}
                    </StatusPill>
                    {tr.status != null && (
                      <StatusPill kind={kindForStatus(tr.status)}>
                        {tr.status}
                        {statusLine.split(" ").slice(1).join(" ") &&
                          ` · ${statusLine.split(" ").slice(1).join(" ")}`}
                      </StatusPill>
                    )}
                    <span className="text-[10px] font-mono text-muted-foreground ml-auto">
                      {new Date(tr.ts).toLocaleString()}
                    </span>
                    <span className="text-[10px] text-muted-foreground select-none opacity-60 group-open:opacity-100">
                      ▾ raw
                    </span>
                  </div>
                  <div className="text-foreground mb-1">{tr.summary || "(no summary)"}</div>
                  <div className="flex flex-wrap gap-3 text-[10px] font-mono text-muted-foreground">
                    {tr.peer_ip && (
                      <span>
                        peer:<span className="text-foreground"> {tr.peer_ip}</span>
                      </span>
                    )}
                    {tr.call_id && (
                      <span className="truncate max-w-[240px]">
                        call-id:<span className="text-foreground"> {tr.call_id}</span>
                      </span>
                    )}
                    {he["From"] && (
                      <span className="truncate max-w-[260px]">
                        from:<span className="text-foreground"> {he.From}</span>
                      </span>
                    )}
                  </div>
                </summary>
                {/* Phase 6g — full expanded view. Surfaces the carrier's
                    reason-phrase, Proxy-Authenticate / WWW-Authenticate
                    challenge, and any captured headers so debugging
                    407-after-retry doesn't require SSH/tcpdump. */}
                <div
                  className="mt-3 pt-2 border-t border-border/40 space-y-2 font-mono text-[11px]"
                  data-testid={`traces-item-${tr.id}-raw`}
                >
                  {statusLine && (
                    <div>
                      <span className="text-muted-foreground">status-line:</span>{" "}
                      <span className="text-foreground">{statusLine}</span>
                    </div>
                  )}
                  {challenge && (
                    <div className="space-y-0.5">
                      <div className="text-muted-foreground">
                        {he["Proxy-Authenticate"] ? "Proxy-Authenticate" : "WWW-Authenticate"}:
                      </div>
                      <pre className="whitespace-pre-wrap break-all bg-muted/30 rounded p-2 leading-relaxed">
                        {challenge}
                      </pre>
                    </div>
                  )}
                  {reasonHdr && (
                    <div>
                      <span className="text-muted-foreground">
                        {he.Reason ? "Reason:" : "Warning:"}
                      </span>{" "}
                      <span className="text-foreground">{reasonHdr}</span>
                    </div>
                  )}
                  {interestingKeys.length === 0 ? (
                    <div className="text-muted-foreground italic">
                      No headers captured for this entry.
                    </div>
                  ) : (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-muted-foreground select-none">
                        all captured headers ({interestingKeys.length})
                      </summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all bg-muted/20 rounded p-2 leading-relaxed">
                        {interestingKeys
                          .map((k) => `${k}: ${he[k]}`)
                          .join("\n")}
                      </pre>
                    </details>
                  )}
                </div>
              </details>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * Right-side Sheet showing recent SIP traces for a party (customer OR trunk).
 * Used by Live Traffic where there's no editor context to embed into.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (bool) => void
 *   party: { id, name, kind }
 */
export function TracesSheet({ open, onOpenChange, party }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="traces-sheet"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-[hsl(var(--accent))]" />
            SIP Traces — {party?.name || party?.id}
          </SheetTitle>
          <SheetDescription>
            Engine-level SIP events for this {party?.kind || "party"} (auth, register, invite,
            codec policy, reject). Last 500 kept per party.
          </SheetDescription>
        </SheetHeader>

        <TracesPane party={party} active={open} />
      </SheetContent>
    </Sheet>
  );
}
